import { useEffect, useState, useMemo, useCallback } from "react";
import { myOrders } from "../../actions/orderAction";
import { useDispatch, useSelector } from "react-redux";

import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Button,
  Paper,
  useTheme,
  Grid,
  List,
  ListItem,
  Divider,
  Collapse,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";

import MinCategory from "../Layouts/MinCategory";
import MetaData from "../Layouts/MetaData";
import OrderItem from "./OrderItem";

/* ================================================= */

const orderStatus = ["Processing", "Shipped", "Delivered"];

const dt = new Date();

const ordertime = [
  { label: "This Month", value: "month" },
  { label: dt.getFullYear() - 1, value: dt.getFullYear() - 1 },
  { label: dt.getFullYear() - 2, value: dt.getFullYear() - 2 },
  { label: "Older", value: "older" },
];

/* ================================================= */

const MyOrders = () => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const { orders = [], loading } = useSelector((state) => state.myOrders);

  const [status, setStatus] = useState("");
  const [orderTime, setOrderTime] = useState("");
  const [search, setSearch] = useState("");

  /* ✅ Native date range */
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [filtersOpen, setFiltersOpen] = useState(true);

  const paperStyle = {
    p: 3,
    borderRadius: 2,
    boxShadow: theme.shadows[3],
  };

  /* ================= FETCH ================= */
  useEffect(() => {
    dispatch(myOrders());
  }, [dispatch]);

  /* =================================================
        🔥 SINGLE FILTER PIPELINE
  ================================================= */

  const filteredOrders = useMemo(() => {
    let result = [...orders];

    /* ---------- STATUS ---------- */
    if (status) {
      result = result.filter(
        (o) =>
          o.orderStatus?.toLowerCase().trim() ===
          status.toLowerCase().trim()
      );
    }

    /* ---------- QUICK TIME FILTER ---------- */
    if (orderTime) {
      if (orderTime === "month") {
        result = result.filter((o) => {
          const d = new Date(o.createdAt);
          return (
            d.getMonth() === dt.getMonth() &&
            d.getFullYear() === dt.getFullYear()
          );
        });
      } else if (orderTime === "older") {
        result = result.filter(
          (o) => new Date(o.createdAt).getFullYear() < dt.getFullYear() - 2
        );
      } else {
        result = result.filter(
          (o) =>
            new Date(o.createdAt).getFullYear() === Number(orderTime)
        );
      }
    }

    /* ---------- DATE RANGE (native inputs) ---------- */
    if (fromDate || toDate) {
      result = result.filter((o) => {
        const orderDate = new Date(o.createdAt);

        if (fromDate && orderDate < new Date(fromDate)) return false;
        if (toDate && orderDate > new Date(toDate)) return false;

        return true;
      });
    }

    /* ---------- SEARCH ---------- */
    if (search.trim()) {
      result = result
        .map((order) => ({
          ...order,
          orderItems:
            order.orderItems?.filter((item) =>
              item.name
                .toLowerCase()
                .includes(search.toLowerCase())
            ) || [],
        }))
        .filter((o) => o.orderItems.length > 0);
    }

    return result;
  }, [orders, status, orderTime, search, fromDate, toDate]);

  /* ================= CLEAR ================= */
  const clearFilters = () => {
    setStatus("");
    setOrderTime("");
    setSearch("");
    setFromDate("");
    setToDate("");
  };

  const toggleFilters = useCallback(() => {
    setFiltersOpen((prev) => !prev);
  }, []);

  /* ================================================= */

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        bgcolor: "background.default",
        alignItems: "center",
        pt: 4,
      }}
    >
      <MetaData title="My Orders - AmritLok" />
      <MinCategory />

      <Box sx={{ width: { xs: "95%", md: "80%", lg: "70%" }, mt: 4, mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
          My Orders
        </Typography>

        <Grid container spacing={3}>
          {/* ================= FILTER PANEL ================= */}
          <Grid item xs={12} md={4} lg={3}>
            <Paper sx={{ ...paperStyle, p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={toggleFilters}
              >
                <Typography variant="h6">Filters</Typography>
                {filtersOpen ? <ExpandLess /> : <ExpandMore />}
              </Box>

              <Collapse in={filtersOpen}>
                <List>
                  {/* SEARCH */}
                  <ListItem>
                    <TextField
                      fullWidth
                      label="Search Orders"
                      size="small"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton>
                              <SearchIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </ListItem>

                  <Divider sx={{ my: 2 }} />

                  {/* STATUS */}
                  <ListItem>
                    <FormControl>
                      <Typography sx={{ mb: 1 }}>Order Status</Typography>
                      <RadioGroup
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        {orderStatus.map((s) => (
                          <FormControlLabel
                            key={s}
                            value={s}
                            control={<Radio />}
                            label={s}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </ListItem>

                  <Divider sx={{ my: 2 }} />

                  {/* QUICK TIME */}
                  <ListItem>
                    <FormControl>
                      <Typography sx={{ mb: 1 }}>Quick Time</Typography>
                      <RadioGroup
                        value={orderTime}
                        onChange={(e) => setOrderTime(e.target.value)}
                      >
                        {ordertime.map((t) => (
                          <FormControlLabel
                            key={t.value}
                            value={t.value}
                            control={<Radio />}
                            label={t.label}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </ListItem>

                  <Divider sx={{ my: 2 }} />

                  {/* DATE RANGE (native) */}
                  <ListItem>
                    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
                      <Typography>Custom Date Range</Typography>

                      <TextField
                        type="date"
                        size="small"
                        label="From"
                        InputLabelProps={{ shrink: true }}
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                      />

                      <TextField
                        type="date"
                        size="small"
                        label="To"
                        InputLabelProps={{ shrink: true }}
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                      />
                    </Box>
                  </ListItem>

                  <ListItem>
                    <Button fullWidth variant="outlined" onClick={clearFilters}>
                      Clear Filters
                    </Button>
                  </ListItem>
                </List>
              </Collapse>
            </Paper>
          </Grid>

          {/* ================= ORDERS ================= */}
          <Grid item xs={12} md={8} lg={9}>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 200,
                }}
              >
                <CircularProgress />
              </Box>
            ) : filteredOrders.length > 0 ? (
              filteredOrders
                .flatMap((order) =>
                  order.orderItems?.map((item, index) => (
                    <OrderItem
                      key={`${order._id}-${index}`}
                      {...item}
                      orderId={order._id}
                      orderStatus={order.orderStatus}
                      createdAt={order.createdAt}
                      deliveredAt={order.deliveredAt}
                      paymentInfo={order.paymentInfo}
                    />
                  )) || []
                )
                .reverse()
            ) : (
              <Paper sx={{ ...paperStyle, textAlign: "center" }}>
                <Typography>No orders found.</Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default MyOrders;
